import { useEffect, useRef, useState, useCallback } from "react";

/**
 * useIdleTimeout — tracks user activity (mouse/keyboard/touch) and fires callbacks
 * when the user has been idle for `warnMs` and again at `idleMs`.
 *
 * Does NOT poll any API or set up repeating timers during activity —
 * just one resettable timeout per phase.
 */
export function useIdleTimeout({ idleMs, warnMs, onTimeout, onWarn, enabled = true }) {
  const [warning, setWarning] = useState(false);
  const warnTimer = useRef(null);
  const idleTimer = useRef(null);
  const onTimeoutRef = useRef(onTimeout);
  const onWarnRef = useRef(onWarn);

  useEffect(() => { onTimeoutRef.current = onTimeout; }, [onTimeout]);
  useEffect(() => { onWarnRef.current = onWarn; }, [onWarn]);

  const clearTimers = () => {
    if (warnTimer.current) clearTimeout(warnTimer.current);
    if (idleTimer.current) clearTimeout(idleTimer.current);
  };

  const reset = useCallback(() => {
    setWarning(false);
    clearTimers();
    if (!enabled) return;
    warnTimer.current = setTimeout(() => {
      setWarning(true);
      onWarnRef.current?.();
    }, warnMs);
    idleTimer.current = setTimeout(() => {
      onTimeoutRef.current?.();
    }, idleMs);
  }, [enabled, warnMs, idleMs]);

  useEffect(() => {
    if (!enabled) { clearTimers(); return; }
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    // Passive listeners, debounced via a 30-second minimum between resets
    // to avoid thrashing timers while the user is actively moving the mouse.
    let lastReset = 0;
    const handler = () => {
      const now = Date.now();
      if (now - lastReset < 30_000) return;
      lastReset = now;
      reset();
    };
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    reset();
    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      clearTimers();
    };
  }, [enabled, reset]);

  return { warning, extend: reset, dismissWarning: () => setWarning(false) };
}
